import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaEdit, FaTrash, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import useColleges from '../../../../utils/useColleges';
import AttendanceNav from '../AttendanceNav';
import '../AttendanceNav.css';
import './Holidays.css';

const Holidays = () => {
  const { user }  = useAuth();
  const isAdmin   = user?.role === 'admin';
  const { colleges, loading: collegesLoading } = useColleges();

  const [holidays, setHolidays]           = useState([]);
  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  const [searchQuery, setSearchQuery]     = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [filterYear, setFilterYear]       = useState(String(new Date().getFullYear()));
  const [filterFromMonth, setFilterFromMonth] = useState('');
  const [filterToMonth, setFilterToMonth]     = useState('');
  const [showAddForm, setShowAddForm]     = useState(false);
  const [editId, setEditId]               = useState(null);
  const [expandedRow, setExpandedRow]     = useState(null);
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm]                   = useState({ date: today, name: '', selectedColleges: [] });

  // college dropdown state
  const [showCollegeDrop, setShowCollegeDrop] = useState(false);
  const [collegeSearch, setCollegeSearch]     = useState('');
  const collegeDropRef = useRef(null);
  const searchRef      = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (collegeDropRef.current && !collegeDropRef.current.contains(e.target)) setShowCollegeDrop(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchQuery('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/holidays');
      setHolidays(data.data || []);
    } catch {
      Swal.fire('Error', 'Failed to load holidays.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => {
    setForm({ date: today, name: '', selectedColleges: [] });
    setCollegeSearch('');
    setShowCollegeDrop(false);
    setEditId(null);
    setShowAddForm(false);
  };

  const validateForm = () => {
    if (isAdmin && form.selectedColleges.length === 0) { Swal.fire('Missing Field', 'Please select at least one college.', 'warning'); return false; }
    if (!form.date) { Swal.fire('Missing Field', 'Please select a date.', 'warning'); return false; }
    if (!form.name.trim()) { Swal.fire('Missing Field', 'Please enter holiday name.', 'warning'); return false; }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (editId) {
        const college = isAdmin ? form.selectedColleges[0] : (user?.college?._id || user?.college);
        const { data } = await api.put(`/holidays/${editId}`, { date: form.date, name: form.name, college });
        setHolidays(prev => prev.map(h => h._id === editId ? data.data : h));
        Swal.fire('Updated!', 'Holiday updated.', 'success');
      } else {
        const collegeIds = isAdmin ? form.selectedColleges : [user?.college?._id || user?.college];
        const results = await Promise.all(
          collegeIds.map(college => api.post('/holidays', { date: form.date, name: form.name, college }))
        );
        const newHolidays = results.map(r => r.data.data);
        setHolidays(prev => [...prev, ...newHolidays].sort((a, b) => new Date(a.date) - new Date(b.date)));
        Swal.fire('Added!', `Holiday created for ${collegeIds.length} college(s).`, 'success');
      }
      resetForm();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (h) => {
    setEditId(h._id);
    setForm({ date: h.date ? h.date.split('T')[0] : '', name: h.name, selectedColleges: [h.college?._id || h.college || ''] });
    setShowAddForm(true);
    setExpandedRow(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (h) => {
    Swal.fire({ title: `Delete "${h.name}"?`, text: 'Cannot be undone.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e74c3c', confirmButtonText: 'Yes, delete' })
      .then(async (res) => {
        if (!res.isConfirmed) return;
        try {
          await api.delete(`/holidays/${h._id}`);
          setHolidays(prev => prev.filter(x => x._id !== h._id));
          Swal.fire('Deleted!', 'Holiday deleted.', 'success');
        } catch (err) {
          Swal.fire('Error', err.response?.data?.message || 'Delete failed.', 'error');
        }
      });
  };

  const toggleCollege = (id) => {
    setForm(f => ({
      ...f,
      selectedColleges: f.selectedColleges.includes(id)
        ? f.selectedColleges.filter(c => c !== id)
        : [...f.selectedColleges, id],
    }));
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric', weekday: 'short' });
  };

  const filteredCollegeOptions = useMemo(() => {
    if (!collegeSearch) return colleges;
    const q = collegeSearch.toLowerCase();
    return colleges.filter(c => c.name.toLowerCase().includes(q));
  }, [colleges, collegeSearch]);

  const selectedCollegeLabel = useMemo(() => {
    if (form.selectedColleges.length === 0) return '-- Select College(s) --';
    if (form.selectedColleges.length === colleges.length && colleges.length > 0) return 'All Colleges Selected';
    if (form.selectedColleges.length === 1) {
      const c = colleges.find(x => x._id === form.selectedColleges[0]);
      return c?.name || '1 selected';
    }
    return `${form.selectedColleges.length} colleges selected`;
  }, [form.selectedColleges, colleges]);

  // Years that actually have holidays — only these shown in dropdown
  const availableYears = useMemo(() => {
    const years = holidays.map(h => new Date(h.date).getFullYear()).filter(Boolean);
    return [...new Set(years)].sort((a, b) => b - a); // descending
  }, [holidays]);

  const MONTHS = [
    { value: '1', label: 'January' }, { value: '2', label: 'February' },
    { value: '3', label: 'March' },   { value: '4', label: 'April' },
    { value: '5', label: 'May' },     { value: '6', label: 'June' },
    { value: '7', label: 'July' },    { value: '8', label: 'August' },
    { value: '9', label: 'September' },{ value: '10', label: 'October' },
    { value: '11', label: 'November' },{ value: '12', label: 'December' },
  ];

  const filtered = useMemo(() => {
    let res = holidays;
    if (searchQuery) { const q = searchQuery.toLowerCase(); res = res.filter(h => h.name.toLowerCase().includes(q)); }
    if (filterCollege) res = res.filter(h => String(h.college?._id || h.college) === filterCollege);
    // Year filter
    if (filterYear) {
      const yr = Number(filterYear);
      res = res.filter(h => new Date(h.date).getFullYear() === yr);
    }
    // From month filter
    if (filterYear && filterFromMonth) {
      const from = Number(filterFromMonth);
      res = res.filter(h => new Date(h.date).getMonth() + 1 >= from);
    }
    // To month filter
    if (filterYear && filterToMonth) {
      const to = Number(filterToMonth);
      res = res.filter(h => new Date(h.date).getMonth() + 1 <= to);
    }
    return res;
  }, [holidays, searchQuery, filterCollege, filterYear, filterFromMonth, filterToMonth]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(h => {
      const collegeId = h.college?._id || h.college;
      const found = colleges.find(c => String(c._id) === String(collegeId));
      const key = found?.name || h.college?.name || 'Unassigned';
      if (!g[key]) g[key] = [];
      g[key].push(h);
    });
    return g;
  }, [filtered, colleges]);

  return (
    <div className="holiday-present-for-employee-container">
      <h2 className="holiday-present-for-employee-title">Manage Holidays</h2>
      <AttendanceNav />

      {/* Controls */}
      <div className="holiday-present-for-employee-controls">
        <div className="holiday-present-for-employee-search-wrapper" ref={searchRef}>
          <FaSearch className="holiday-present-for-employee-search-icon" />
          <input type="text" className="holiday-present-for-employee-search-input"
            placeholder="Search holiday name..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>

        {isAdmin && (
          <select className="holiday-present-for-employee-filter-select"
            value={filterCollege} onChange={e => setFilterCollege(e.target.value)}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        {/* Year filter — only years with holidays */}
        <select className="holiday-present-for-employee-filter-select"
          value={filterYear}
          onChange={e => { setFilterYear(e.target.value); setFilterFromMonth(''); setFilterToMonth(''); }}>
          <option value="">All Years</option>
          {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        {/* From Month */}
        {filterYear && (
          <select className="holiday-present-for-employee-filter-select"
            value={filterFromMonth} onChange={e => setFilterFromMonth(e.target.value)}>
            <option value="">From Month</option>
            {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        )}

        {/* To Month */}
        {filterYear && filterFromMonth && (
          <select className="holiday-present-for-employee-filter-select"
            value={filterToMonth} onChange={e => setFilterToMonth(e.target.value)}>
            <option value="">To Month</option>
            {MONTHS.filter(m => Number(m.value) >= Number(filterFromMonth))
              .map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        )}

        <button className="holiday-present-for-employee-btn holiday-present-for-employee-btn-add"
          onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}>
          <FaPlus /> {showAddForm ? 'Close Form' : 'Add Holiday'}
        </button>
      </div>

      {/* Form */}
      {showAddForm && (
        <div className="holiday-present-for-employee-inline-form">
          <h3 className="holiday-present-for-employee-form-title">
            {editId ? '✏️ Edit Holiday' : 'Create New Holiday'}
          </h3>
          <div className="holiday-present-for-employee-form-grid">

            {/* 1. College dropdown with checkboxes — admin only */}
            {isAdmin && (
              <div className="holiday-present-for-employee-form-group" ref={collegeDropRef}>
                <label>College(s) <span className="holiday-present-for-employee-req">*</span>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 400, marginLeft: 6 }}>select multiple for same holiday</span>
                </label>
                <div className="holiday-college-dropdown-wrapper">
                  <div className={`holiday-college-select-box ${showCollegeDrop ? 'open' : ''}`}
                    onClick={() => setShowCollegeDrop(v => !v)}>
                    <span style={{ color: form.selectedColleges.length ? '#1e293b' : '#94a3b8' }}>
                      {selectedCollegeLabel}
                    </span>
                    <FaChevronDown style={{ fontSize: '0.75rem', color: '#64748b', transition: '0.2s', transform: showCollegeDrop ? 'rotate(180deg)' : 'none' }} />
                  </div>
                  {showCollegeDrop && (
                    <div className="holiday-college-dropdown">
                      <div className="holiday-college-search">
                        <FaSearch style={{ color: '#94a3b8', fontSize: '0.8rem' }} />
                        <input type="text" placeholder="Search college..." value={collegeSearch}
                          onChange={e => setCollegeSearch(e.target.value)} autoFocus />
                        {collegeSearch && <FaTimes style={{ cursor: 'pointer', color: '#94a3b8' }} onClick={() => setCollegeSearch('')} />}
                      </div>
                      <div className="holiday-college-options">
                        {filteredCollegeOptions.length === 0
                          ? <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: '0.85rem' }}>No colleges found</div>
                          : <>
                            <label className={`holiday-college-option ${form.selectedColleges.length === colleges.length ? 'selected' : ''}`}>
                              <input type="checkbox"
                                checked={form.selectedColleges.length === colleges.length}
                                onChange={() => setForm(f => ({
                                  ...f,
                                  selectedColleges: f.selectedColleges.length === colleges.length ? [] : colleges.map(c => c._id)
                                }))}
                              />
                              <span style={{ fontWeight: 700 }}>Select All</span>
                            </label>
                            <div style={{ borderTop: '1px solid #e2e8f0' }} />
                            {filteredCollegeOptions.map(c => (
                              <label key={c._id} className={`holiday-college-option ${form.selectedColleges.includes(c._id) ? 'selected' : ''}`}>
                                <input type="checkbox" checked={form.selectedColleges.includes(c._id)}
                                  onChange={() => toggleCollege(c._id)} />
                                <span>{c.name}</span>
                              </label>
                            ))}
                          </>
                        }
                      </div>
                      {form.selectedColleges.length > 0 && (
                        <div className="holiday-college-footer">
                          {form.selectedColleges.length} selected
                          <button onClick={() => setForm(f => ({ ...f, selectedColleges: [] }))}>Clear all</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 2. Date */}
            <div className="holiday-present-for-employee-form-group">
              <label>Date <span className="holiday-present-for-employee-req">*</span></label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>

            {/* 3. Holiday Name */}
            <div className="holiday-present-for-employee-form-group">
              <label>Holiday Name <span className="holiday-present-for-employee-req">*</span></label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Diwali, Christmas, Republic Day" />
            </div>

          </div>

          <div className="holiday-present-for-employee-form-buttons">
            <button className="holiday-present-for-employee-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="holiday-present-for-employee-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update Holiday' : 'Create Holiday'}
            </button>
          </div>
        </div>
      )}

      {/* Table grouped by college */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading holidays...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', background: '#fff', borderRadius: '8px' }}>No holidays found.</div>
      ) : (
        <div className="holiday-grouped-container">
          {Object.entries(grouped).map(([collegeName, items]) => (
            <div key={collegeName} className="holiday-table-group">
              <h3 className="holiday-group-header">🏫 {collegeName}</h3>
              <div className="holiday-present-for-employee-table-wrapper">
                <table className="holiday-present-for-employee-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th className="holiday-present-for-employee-align-left">Holiday Name</th>
                      <th className="holiday-table-col-date">Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((h, i) => (
                      <React.Fragment key={h._id}>
                        <tr className="holiday-present-for-employee-main-row">
                          <td>{i + 1}</td>
                          <td className="holiday-present-for-employee-align-left">
                            <div className="holiday-present-for-employee-name-wrapper"
                              onClick={() => setExpandedRow(expandedRow === h._id ? null : h._id)}>
                              <span>{h.name}</span>
                              <button className="holiday-present-for-employee-expand-btn">
                                {expandedRow === h._id ? <FaChevronUp /> : <FaChevronDown />}
                              </button>
                            </div>
                          </td>
                          <td className="holiday-table-col-date">
                            <span className="holiday-present-for-employee-badge">{formatDate(h.date)}</span>
                          </td>
                          <td>
                            <div className="holiday-present-for-employee-action-btns">
                              <button className="holiday-present-for-employee-btn-edit" onClick={() => handleEdit(h)}><FaEdit /></button>
                              <button className="holiday-present-for-employee-btn-delete" onClick={() => handleDelete(h)}><FaTrash /></button>
                            </div>
                          </td>
                        </tr>
                        {expandedRow === h._id && (
                          <tr className="holiday-present-for-employee-expanded-row">
                            <td colSpan="4">
                              <div className="holiday-present-for-employee-expanded-content">
                                <div className="holiday-present-for-employee-expanded-item">
                                  <span className="holiday-present-for-employee-exp-label">Date</span>
                                  <span className="holiday-present-for-employee-exp-value">{formatDate(h.date)}</span>
                                </div>
                                <div className="holiday-present-for-employee-expanded-item">
                                  <span className="holiday-present-for-employee-exp-label">College</span>
                                  <span className="holiday-present-for-employee-exp-value">{collegeName}</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Holidays;
